import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { CheckCircle } from "lucide-react";
import { useShop } from "../context/ShopContext";
import Seo from "../components/Seo";
import LandingShell from "../components/LandingShell";
import { captureUtm, trackWithConsent } from "../lib/metaPixel";

// Landing per le campagne social. Il form e' il Web-to-Lead di Perfex CRM
// (ideamediacrm.com): i contatti finiscono direttamente nel CRM.
// Una chiave per sorgente (utm_source) permette a Perfex di assegnare la
// "Lead source" giusta: aggiungere qui le chiavi dei form creati in Perfex.
const FORM_KEYS = {
  default: "eed963a3ce787a59db35a940d6dd78b9",
  // instagram: "...", facebook: "...", tiktok: "...",
};
const FORM_BASE = "https://ideamediacrm.com/forms/wtl/";
// Altezza misurata del form Perfex (597px a ogni larghezza) + margine per i
// messaggi di validazione. scrolling resta "auto": se Perfex aggiunge righe
// compare una barra invece di tagliare il pulsante Invia.
const FORM_HEIGHT = 640;

const content = {
  it: {
    seoTitle: "Analisi gratuita della tua scheda Google",
    seoDesc: "Scopri come appare la tua attività su Google rispetto ai 3 concorrenti che oggi prendono le chiamate nella tua zona. Analisi gratuita in PDF, senza impegno.",
    eyebrow: "Analisi gratuita · senza impegno",
    h1: "Scopri come appare la tua attività su Google rispetto ai 3 concorrenti che oggi prendono le chiamate nella tua zona",
    sub: "Analisi gratuita della tua scheda Google, in PDF. Nessuna newsletter, nessun impegno.",
    trust: "Ti rispondo io personalmente, di solito entro la giornata.",
    trustName: "Christian Mino, IdeaMedia",
    formTitle: "Richiedi l'analisi gratuita",
    consent: "Inviando il modulo accetti di essere ricontattato per ricevere l'analisi.",
    privacy: "Privacy policy",
    getTitle: "Cosa ricevi",
    get: [
      "La tua scheda confrontata con i 3 della mappa (recensioni, foto, categorie, orari)",
      "Cosa manca nella tua scheda, con punteggio da 1 a 10",
      "Le 3 azioni da fare subito, anche da solo",
    ],
    howTitle: "Come funziona",
    how: [
      "Compili il modulo (30 secondi)",
      "Preparo l'analisi",
      "Te la mando su WhatsApp o email, di solito entro la giornata",
    ],
    cta: "Richiedi l'analisi gratuita",
    iframeTitle: "Modulo di richiesta analisi gratuita",
  },
  en: {
    seoTitle: "Free analysis of your Google listing",
    seoDesc: "See how your business looks on Google compared to the 3 competitors taking the calls in your area today. Free PDF analysis, no commitment.",
    eyebrow: "Free analysis · no commitment",
    h1: "See how your business looks on Google compared to the 3 competitors taking the calls in your area today",
    sub: "Free analysis of your Google listing, as a PDF. No newsletter, no commitment.",
    trust: "I answer you personally, usually within the day.",
    trustName: "Christian Mino, IdeaMedia",
    formTitle: "Request the free analysis",
    consent: "By sending the form you agree to be contacted to receive the analysis.",
    privacy: "Privacy policy",
    getTitle: "What you get",
    get: [
      "Your listing compared with the 3 on the map (reviews, photos, categories, hours)",
      "What is missing in your listing, with a score from 1 to 10",
      "The 3 actions to take right away, even on your own",
    ],
    howTitle: "How it works",
    how: [
      "Fill in the form (30 seconds)",
      "I prepare the analysis",
      "I send it to you on WhatsApp or email, usually within the day",
    ],
    cta: "Request the free analysis",
    iframeTitle: "Free analysis request form",
  },
};

const AnalisiGoogle = () => {
  const { lang } = useShop();
  const c = content[lang];
  const location = useLocation();
  const formRef = useRef(null);
  const [formVisible, setFormVisible] = useState(true);
  const [utm, setUtm] = useState({});

  useEffect(() => {
    setUtm(captureUtm(location.search));
    trackWithConsent([["PageView"]]);
  }, [location.search]);

  // Pulsante sticky: visibile solo finche' il form e' fuori dallo schermo.
  useEffect(() => {
    const el = formRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const obs = new IntersectionObserver(([entry]) => setFormVisible(entry.isIntersecting), { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const key = FORM_KEYS[(utm.utm_source || "").toLowerCase()] || FORM_KEYS.default;
  const qs = new URLSearchParams(utm).toString();
  const formUrl = FORM_BASE + key + (qs ? `?${qs}` : "");
  const privacyPath = lang === "it" ? "/pagine/privacy-policy" : "/en/pages/privacy-policy";

  return (
    <LandingShell pixelEvents={[["PageView"]]}>
      <Seo title={c.seoTitle} description={c.seoDesc} image="/images/og-analisi-google.jpg" noindex />

      {/* Su mobile: titolo → form → benefici. Da 1024px la griglia CSS mette
          titolo e benefici a sinistra e il form, sticky, a destra. */}
      <main className="idm-lp-wrap idm-lp-grid" data-testid="analisi-google-page">
        <div className="idm-lp-hero">
          <p className="idm-lp-eyebrow">{c.eyebrow}</p>
          <h1>{c.h1}</h1>
          <p className="idm-lp-sub">{c.sub}</p>

          <div className="idm-lp-trust">
            <img src="/images/christian-56.webp" alt="Christian Mino" width="56" height="56" loading="eager" fetchpriority="high" />
            <p>{c.trust}<strong>— {c.trustName}</strong></p>
          </div>
        </div>

        <section ref={formRef} id="form" className="idm-lp-formsec" aria-label={c.formTitle}>
          <h2 className="idm-lp-formtitle">{c.formTitle}</h2>
          <div className="idm-lp-form" data-testid="analisi-google-form">
            <iframe
              title={c.iframeTitle}
              src={formUrl}
              width="100%"
              height={FORM_HEIGHT}
              style={{ height: FORM_HEIGHT }}
              sandbox="allow-top-navigation allow-forms allow-scripts allow-same-origin allow-popups"
              loading="lazy"
            />
          </div>
          <p className="idm-lp-consent">
            {c.consent} <a href={privacyPath} target="_blank" rel="noreferrer">{c.privacy}</a>.
          </p>
        </section>

        <div className="idm-lp-more">
        <section className="idm-lp-section">
          <h2>{c.getTitle}</h2>
          <ul className="idm-lp-list">
            {c.get.map((s) => (
              <li key={s}><CheckCircle size={20} aria-hidden="true" /><span>{s}</span></li>
            ))}
          </ul>
        </section>

        <section className="idm-lp-section">
          <h2>{c.howTitle}</h2>
          <ol className="idm-lp-list idm-lp-steps">
            {c.how.map((s, i) => (
              <li key={s}><span className="idm-lp-num" aria-hidden="true">{i + 1}</span><span>{s}</span></li>
            ))}
          </ol>
        </section>
        </div>
      </main>

      <div className="idm-lp-sticky" hidden={formVisible}>
        <button type="button" onClick={scrollToForm} data-testid="sticky-cta">{c.cta}</button>
      </div>
    </LandingShell>
  );
};

export default AnalisiGoogle;
